import { ReactNode, useRef } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import * as SelectPrimitive from '@rn-primitives/select';
import { styles } from './SelectMenuField.styles';

export function SelectMenuField<T extends NonNullable<SelectPrimitive.Option>>({
  className,
  options,
  renderItem,
  footer,
}: ISelectMenuProps<T>) {
  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  };
  const { triggerPosition } = SelectPrimitive.useRootContext();
  const contentWidth =
    Platform.OS !== 'web' ? triggerPosition?.width : undefined;

  const container = useRef(
    Platform.OS === 'web'
      ? document.getElementById('primitives-root')
      : undefined
  );

  return (
    <SelectPrimitive.Portal container={container.current} hostName="root">
      <SelectPrimitive.Overlay
        style={StyleSheet.absoluteFill}
        className="z-[1]"
      >
        <SelectPrimitive.Content
          sideOffset={8}
          insets={contentInsets}
          className={styles.content({ className })}
          style={{
            width: contentWidth,
          }}
        >
          <SelectPrimitive.ScrollUpButton />
          <SelectPrimitive.Viewport className={styles.viewport}>
            <SelectPrimitive.Group>
              <ScrollView className={styles.group}>
                {options.map((option, index) => {
                  return (
                    <SelectPrimitive.Item
                      key={index}
                      label={option.label}
                      value={option.value}
                      className={styles.item}
                    >
                      <Stack
                        horizontal
                        align="center"
                        justify="between"
                        gap="sm"
                      >
                        {/* Radix-UI needs this ItemText to create the native <option> elements.
                        We are hiding it because we have a custom way to render the item */}
                        {Platform.OS === 'web' && (
                          <SelectPrimitive.ItemText className="hidden" />
                        )}
                        {renderItem ? (
                          renderItem(option)
                        ) : (
                          <BodyText bold>{option.label}</BodyText>
                        )}
                        <View className={styles.itemIndicator}>
                          <SelectPrimitive.ItemIndicator>
                            <Icon
                              icon={'check'}
                              color="systemSuccess"
                              size={16}
                            />
                          </SelectPrimitive.ItemIndicator>
                        </View>
                      </Stack>
                    </SelectPrimitive.Item>
                  );
                })}
              </ScrollView>
              {footer && <View className={styles.footer}>{footer}</View>}
            </SelectPrimitive.Group>
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Overlay>
    </SelectPrimitive.Portal>
  );
}

export interface ISelectMenuProps<
  T extends NonNullable<SelectPrimitive.Option>,
> {
  className?: string;
  options: T[];
  renderItem?: (item: T) => ReactNode;
  footer?: ReactNode;
}
